<template>
  <section
    v-if="isSelectionVisible"
    :class="selectionBlockClasses"
  >
    <SkeletonBlock :is-active="isSkeletonActive">
      <template
        v-if="!props.isExtraSelection"
        #skeleton
      >
        <ExpCardSliderSkeleton
          v-for="i in skeletonSlidersCount"
          :key="`selection-skeleton-${i}`"
          class="exp-selection-skeleton"
          :is-animated="isAnimated"
        />
      </template>

      <ExcursionExpSelectionGrid
        v-if="!isCityTagsCollectionsExperimentActive && gridSelection?.length"
        :selection="gridSelection[0]"
        :context-geo-subject="contextGeoSubject"
      />
      <template v-for="(selection, index) in gridSliderWithVisibleLabels">
        <ExpCardSlider
          v-if="showExpSlider(selection)"
          :key="`selection-${index}`"
          :class="expCardSliderCssClass"
          :selection="selection"
          :is-mousewheel-enabled="false"
          sogu-block="section"
          sogu-page="listing"
          :favorite-list="Array.from(favoritesStore.favoriteIds)"
          :context-geo-subject="contextGeoSubject"
          :location="props.location"
          :position="position"
          :tooltip-teleport-target="targetModalElem"
          :is-minified-on-mobile="isCityTagsCollectionsExperimentActive"
          :is-subtitle-above-title="isCityTagsCollectionsExperimentActive && props.isInjection"
          cardSize="l"
          :showMiniDiscount="isExpCardMiniDiscountActive"
          @toggle-favorite="toggleFavoriteHandle($event, selection)"
        />
      </template>
    </SkeletonBlock>
  </section>
</template>

<script setup lang="ts">
  // Widgets
  import ExcursionExpSelectionGrid from '@/widgets/expSelectionsBlock/ui/excursion/ExcursionExpSelectionGrid.vue';
  import { useExcursionSelectionBlock } from '@/widgets/expSelectionsBlock/composables/excursion/useExcursionSelectionBlock.ts';

  // Features
  import { useToggleExperienceFavorite } from '@/features/experience-favorite';
  import { useExcursionExperienceFavoriteSogu } from '@/features/experience-favorite/composables/excursion/useExcursionExperienceFavoriteSogu.ts';
  import { useListingLabelsV4Experiment } from '@/features/experiments';
  import { useExpCardMiniDiscount } from '@/features/experiments/composables/useExpCardMiniDiscount';

  // Entities
  import type { IExpSelection } from '@/entities/expSelection/model/expSelection.ts';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { type LocationType } from '@/entities/location';

  // Shared
  import { type ContextGeoSubject } from '@/shared/types/geography';
  import { useSkeletonPrefetchStore } from '@/shared/store/useSkeletonPrefetchStore.ts';

  // External and shared packages
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { ExpCardSlider, ExpCardSliderSkeleton } from '@experience/exp-card';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import { computed, type useShadowRoot } from 'vue';
  import { useHead } from '@unhead/vue';

  interface IExpCollectionsBlockProps {
    slug: string;
    location: LocationType;
    contextGeoSubject: ContextGeoSubject;
    page: number;
    isExtraSelection: boolean;
    position: 'top' | 'bottom';
    selectionIndex?: number;
    isCityTagsCollectionsExperimentActive?: boolean;
    isInjection?: boolean;
  }

  const route = useRoute();
  const isPageLoaded = ref(sessionStorage.getItem('skeleton-lib:page-loaded') === 'true');

  watch(
    () => route.path,
    () => {
      isPageLoaded.value = sessionStorage.getItem('skeleton-lib:page-loaded') === 'true';
    },
  );

  const isAnimated = computed(() => isPageLoaded.value);
  const props = defineProps<IExpCollectionsBlockProps>();

  const MIN_EXP_AMOUNT = 2;
  const DEFAULT_SELECTIONS_SKELETON_COUNT = 1;

  const { getExpSelectionSkeleton } = useSkeletonPrefetchStore();

  const skeletonSlidersCount = computed(() => {
    return getExpSelectionSkeleton(props.location) ?? DEFAULT_SELECTIONS_SKELETON_COUNT;
  });

  const { selectionsWithClearedMovementType, isShowBlock, isFetched } = useExcursionSelectionBlock(props);
  const { trackSectionFavoriteClick } = useExcursionExperienceFavoriteSogu();
  const favoritesStore = useExperienceFavorites();
  const { toggle } = useToggleExperienceFavorite();
  const { getVisibleListingLabels } = useListingLabelsV4Experiment();
  const { isExpCardMiniDiscountActive } = useExpCardMiniDiscount();

  const isSelectionVisible = computed(() => {
    return isShowBlock.value && (gridSlider.value.length || isSkeletonActive.value);
  });

  const isSkeletonActive = computed(() => !isFetched.value);

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const modalTarget = ref<HTMLElement | null>(null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  }

  const selectionBlockClasses = computed(() => {
    return [
      'exp-selection-block',
      {
        'exp-selection-block--citytags-collections': props.isCityTagsCollectionsExperimentActive,
      },
      {
        'exp-selection-block--citytags-collections-injection':
          props.isCityTagsCollectionsExperimentActive && props.isInjection,
      },
    ];
  });

  const targetModalElem = computed(() => modalTarget.value || document.body);

  const gridSelection = computed(() =>
    selectionsWithClearedMovementType.value.filter((item) => item?.type === 'tag_to_city'),
  );
  const gridSlider = computed(() => {
    const list = selectionsWithClearedMovementType.value.filter((item) => item?.type !== 'tag_to_city');

    if (props.isCityTagsCollectionsExperimentActive && props.isInjection) {
      return list.slice(props.selectionIndex ?? 0, (props.selectionIndex ?? 0) + 1);
    }

    return list;
  });

  const getSelectionWithVisibleLabels = (selection: IExpSelection): IExpSelection => ({
    ...selection,
    experiences: selection.experiences.map((experience) => ({
      ...experience,
      labels: getVisibleListingLabels(experience.labels),
    })),
  });

  const gridSliderWithVisibleLabels = computed(() => gridSlider.value.map(getSelectionWithVisibleLabels));

  const showExpSlider = (exp: IExpSelection) => exp.experiences.length > MIN_EXP_AMOUNT;

  // Сохраняет SOGU-клик секции и передаёт полный объект экскурсии в единый favorite toggle.
  const toggleFavoriteHandle = (experience: Experience, selection: IExpSelection) => {
    if (!experience || !experience.id) {
      return;
    }

    trackSectionFavoriteClick(experience, props.contextGeoSubject, selection);
    toggle(experience);
  };

  const shouldPreloadFirstImage = computed(() => {
    return !props.isExtraSelection && props.position === 'top' && !props.isInjection;
  });

  // Preload оставляем только для верхней подборки, чтобы нижние блоки не конкурировали с первым экраном.
  const firstImageUrl = computed(() => {
    if (!shouldPreloadFirstImage.value) {
      return '';
    }

    const firstSlider = gridSlider.value.find((selection) => showExpSlider(selection));

    return firstSlider?.experiences?.[0]?.photos?.[0]?.thumbnail_l;
  });

  const expCardSliderCssClass = computed(() => {
    const out = 'exp-card-slider';

    if (props.isCityTagsCollectionsExperimentActive) {
      return ' exp-card-slider__no-padding';
    }

    return out;
  });

  // В head добавляется только один кандидат на high-priority загрузку изображения.
  useHead({
    link: () => {
      if (!firstImageUrl.value) {
        return [];
      }

      return [
        {
          rel: 'preload',
          as: 'image',
          href: firstImageUrl.value,
          fetchpriority: 'high',
        },
      ];
    },
  });
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .exp-selection-block {
    padding: 48px 0 0;

    :deep(.exp-selection:not(:first-child)),
    :deep(.exp-selection-skeleton:not(:first-child)) {
      margin-top: 48px;
    }

    &--citytags-collections {
      :deep(.rating-wrap:before) {
        background: none;
      }
    }

    &--citytags-collections-injection {
      :deep(.feature-title.feature-title_selections .feature-title__link) {
        display: flex;
        justify-content: space-between;

        .arrow {
          min-width: 20px;
        }
      }
    }

    @include adaptivity.tablet {
      padding: 40px 0 0;

      + :deep(.bottom) {
        .sorting-and-count {
          padding-top: 0;
        }
      }
    }

    @include adaptivity.mobile {
      padding-top: 40px;
    }
  }
</style>
